import { CAMERA_PRESENTER } from '@studio/constants/Sources';
import { RESET_USER_VIRTUAL_BACKGROUND } from '@studio/constants/VirtualBackground';
import { PASSED } from '@studio/constants/ReadinessCheck';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { GenericCameraSourceSettings } from '@shoflo/polymer/components/SourceSettings/GenericCameraSourceSettings/GenericCameraSourceSettings';
import { AgoraCameraPreview } from '@shoflo/polymer/components/Join/AgoraCameraPreview/AgoraCameraPreview';
import { CameraSourceSettings } from '@shoflo/polymer/components/SourceSettings/CameraSourceSettings/CameraSourceSettings';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AUDIO_VIDEO_SETTINGS_FORM } from 'constants/ModalTypes';
import { ModalBody } from 'reactstrap';
import setUserBackground from '@shoflo/studio-redux/actions/backgroundFilters/setUserBackground';
import setAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setAudioInputDevice';
import setAudioOutputDevice from '@shoflo/studio-redux/actions/meeting/setAudioOutputDevice';
import setVideoInputDevice from '@shoflo/studio-redux/actions/meeting/setVideoInputDevice';
import setTransformedVideoInputs from '@shoflo/studio-redux/actions/meeting/setTransformedVideoInputs';
import addToAttendeeTiles from '@shoflo/studio-redux/actions/meeting/addToAttendeeTiles';
import clearModal from '@shoflo/studio-redux/actions/modal/clearModal/clearModal';
import uploadUserVirtualBackground from '@shoflo/studio-redux/actions/user/uploadUserVirtualBackground';
import deleteUserVirtualBackground from '@shoflo/studio-redux/actions/user/deleteUserVirtualBackground';
import updatePresenterSettings from 'actions/Broadcast/presenters/updatePresenterSettings';
import { StyledModal } from './SourceSettings.styled';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import {
  clearVirtualBackground,
  getVideoInput,
  initializeVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';
import { switchAudioInput } from '@cvent/studio-agora/audio/audioHelper';

const SourceSettings = () => {
  const modal = useSelector(state => state.modal);
  const meeting = useSelector(state => state.meeting, shallowEqual);
  const attendeeCameraStatus = useSelector(
    state => state.attendeeCameraStatus,
    shallowEqual
  );
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const user = useSelector(state => state.user, shallowEqual);
  const cameraStatus = useSelector(state => state.readinessCheck.camera);

  const dispatch = useDispatch();

  const isCameraAvailable = cameraStatus === PASSED;

  const isPresenterEditForm = modal?.type === AUDIO_VIDEO_SETTINGS_FORM;
  if (!isPresenterEditForm || !modal.initialValue) return null;

  const { meetingSession, attendeeResponse } = meeting;

  const closeModal = () => dispatch(clearModal(modal.type));

  const showThisModal =
    isPresenterEditForm && modal.initialValue?.type === CAMERA_PRESENTER;

  const useAgora = shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput } = initialDataPayload;

    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    const attendeeId = attendeeResponse?.Attendee?.AttendeeId ?? '';

    if (useAgora) {
      const { videoTrack } = initialDataPayload;
      if (videoTrack) {
        dispatch(addToAttendeeTiles(attendeeId, videoTrack));
      }
    }

    closeModal();
  };

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      title,
      description,
      userBackground,
      transformedVideoInputs,
      videoInput
    } = data;

    const attendeeId = attendeeResponse?.Attendee?.AttendeeId ?? '';

    if (useAgora) {
      const { videoTrack } = data;
      if (videoTrack) {
        dispatch(addToAttendeeTiles(attendeeId, videoTrack));
      }
      dispatch(setGenericAudioInputDevice(audioInputDeviceId));
    }

    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    const isCameraOn = attendeeCameraStatus[attendeeId] ?? false;
    if (!isCameraOn && !useAgora) {
      meetingSession.audioVideo.stopVideoInput();
    }

    dispatch(
      updatePresenterSettings(modal.initialValue, {
        source_title: title,
        description
      })
    );
    closeModal();
  };

  const { title, description = '' } = modal.initialValue;
  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  if (useAgora) {
    const { client } = meeting;
    return (
      <StyledModal isOpen={showThisModal} backdrop="static" keyboard="false">
        <ModalBody className="p-1">
          <GenericCameraSourceSettings
            title={title}
            description={description}
            onSubmit={onSubmit}
            onCancel={onCancel}
            audioInputDeviceId={audioInputDevice}
            audioOutputDeviceId={audioOutputDevice}
            videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
            isCameraAvailable={isCameraAvailable}
            userBackground={userBackground}
            transformedVideoInputs={transformedVideoInputs}
            uploadUserVirtualBackground={handleUploadUserVirtualBackground}
            deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
            resetUserVirtualBackground={handleResetUserVirtualBackground}
            user={user}
            CameraPreviewElement={AgoraCameraPreview}
            clearVirtualBackground={clearVirtualBackground}
            initializeBackgroundFilter={initializeVirtualBackground}
            getVideoInput={getVideoInput}
            switchAudioInput={switchAudioInput}
            client={client}
          />
        </ModalBody>
      </StyledModal>
    );
  }

  return (
    <StyledModal isOpen={showThisModal} backdrop="static" keyboard="false">
      <ModalBody className="p-1">
        <CameraSourceSettings
          meetingSession={meetingSession}
          title={title}
          description={description}
          onSubmit={onSubmit}
          onCancel={onCancel}
          audioInputDeviceId={audioInputDevice}
          audioOutputDeviceId={audioOutputDevice}
          videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
          isCameraAvailable={isCameraAvailable}
          userBackground={userBackground}
          setUserBackground={setUserBackground}
          transformedVideoInputs={transformedVideoInputs}
          uploadUserVirtualBackground={handleUploadUserVirtualBackground}
          deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
          resetUserVirtualBackground={handleResetUserVirtualBackground}
          user={user}
        />
      </ModalBody>
    </StyledModal>
  );
};

export { SourceSettings };
